import { SyncOutlined } from '@ant-design/icons';
import { Button } from '@components/Button/Button';
import { Modal } from '@components/common/Modal/Modal';
import { PageTitle } from '@components/common/PageTitle/PageTitle';
import {
  Alert,
  Card as AntCard,
  Collapse,
  DatePicker,
  Descriptions,
  Empty,
  Input,
  Result,
  Spin,
  Typography,
} from 'antd';
import locale from 'antd/es/date-picker/locale/ru_RU';
import { Dayjs } from 'dayjs';
import _ from 'lodash';
import { useEffect, useMemo, useState } from 'react';
import { Link, useBlocker, useNavigate, useParams } from 'react-router';

import {
  useGetAutopsyActAudioQuery,
  useGetAutopsyActQuery,
  useProcessAutopsyActMutation,
  useReplaceAutopsyActAudioMutation,
  useSignAutopsyActMutation,
  useUpdateAutopsyActMutation,
} from '@/api/pathology/api';
import { AutopsyActSection } from '@/api/pathology/types';
import { ACTS, PATHOLOGY, REGISTRY, VACCIANTION } from '@/constants/routes';
import { AutopsyActSections } from '@/pages/Pathology/components/card/AutopsyActSections';
import {
  ActStatusTag,
  ProcessingStageTag,
} from '@/pages/Pathology/components/common/ActTags';
import { audioSourceSchema } from '@/pages/Pathology/components/common/audioSource.config';
import {
  AudioSourceValues,
  formatDate,
  formatDateTime,
  PROCESSING_POLLING_INTERVAL,
  toAudioAttachment,
} from '@/pages/Pathology/helpers';
import { useAutopsyActPrint } from '@/pages/Pathology/hooks/useAutopsyActPrint';
import { useAppNotification } from '@/shared/hooks/useAppNotification';
import {
  AutopsyActStatus,
  AutopsySource,
  isProcessingInProgress,
  ProcessingStage,
  ProcessingStageLabel,
} from '@/shared/pathology/enums';
import { hasPermission } from '@/shared/security/hasPermission';
import { pathology } from '@/shared/security/permissions/pathology';
import { withSecurity } from '@/shared/security/withSecurity';
import DateMapper, { DateTimeFormat } from '@/utils/dateUtils';

import cl from './styles.module.scss';

// Несохранённые правки акта; undefined — поле не менялось и берётся из ответа сервера
interface ActDraft {
  performedAt?: string;
  notes?: string;
  transcript?: string;
  sections: Record<string, string>;
}

const EMPTY_DRAFT: ActDraft = { sections: {} };

const SIGN_CONFIRM_TEXT =
  'После подписания редактирование недоступно, особь будет переведена в статус «Падеж». Подписать акт?';

const LEAVE_CONFIRM_TEXT =
  'В акте есть несохранённые изменения. Покинуть страницу без сохранения?';

export const AutopsyActCard = withSecurity(({ auth }) => {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const { showSuccess } = useAppNotification();

  const [pollingInterval, setPollingInterval] = useState<number>(0);
  const [draft, setDraft] = useState<ActDraft>(EMPTY_DRAFT);
  const [isSignModalOpen, setIsSignModalOpen] = useState<boolean>(false);
  const [isAudioModalOpen, setIsAudioModalOpen] = useState<boolean>(false);
  const [audioUrl, setAudioUrl] = useState<string | null>(null);

  const {
    data: act,
    isLoading,
    isError,
  } = useGetAutopsyActQuery(
    { id },
    { skip: !id, pollingInterval, skipPollingIfUnfocused: true },
  );
  const { data: audio, isFetching: isAudioFetching } = useGetAutopsyActAudioQuery(
    { id },
    { skip: !act?.hasAudio },
  );
  const [updateAct, { isLoading: isSaving }] = useUpdateAutopsyActMutation();
  const [signAct, { isLoading: isSigning }] = useSignAutopsyActMutation();
  const [processAct, { isLoading: isRestarting }] = useProcessAutopsyActMutation();
  const [replaceAudio] = useReplaceAutopsyActAudioMutation();
  const { print, printingId } = useAutopsyActPrint();

  const stage = act?.processingStage ?? act?.job?.stage ?? null;
  const processingError = act?.processingError ?? act?.job?.error ?? null;
  const isProcessing = isProcessingInProgress(stage);

  // Пока запись обрабатывается, карточка опрашивается; на DONE/FAILED опрос прекращается
  useEffect(() => {
    setPollingInterval(isProcessing ? PROCESSING_POLLING_INTERVAL : 0);
  }, [isProcessing]);

  useEffect(() => {
    setDraft(EMPTY_DRAFT);
  }, [id]);

  useEffect(() => {
    if (!audio || !act?.hasAudio) {
      setAudioUrl(null);
      return;
    }

    const url = URL.createObjectURL(audio);
    setAudioUrl(url);

    return () => URL.revokeObjectURL(url);
  }, [audio, act?.hasAudio]);

  const isDraft = act?.status === AutopsyActStatus.DRAFT;

  const can = useMemo(
    () => ({
      edit: isDraft && hasPermission(auth, { permissions: pathology.act.edit }),
      sign: isDraft && hasPermission(auth, { permissions: pathology.act.sign }),
      print: hasPermission(auth, { permissions: pathology.act.print }),
    }),
    [auth, isDraft],
  );

  // Разделы и расшифровку не правим, пока идёт обработка: сервер их перезапишет
  const canEditContent = can.edit && !isProcessing;

  const isDirty =
    draft.performedAt !== undefined ||
    draft.notes !== undefined ||
    draft.transcript !== undefined ||
    !_.isEmpty(draft.sections);

  const sections = useMemo(() => _.sortBy(act?.sections ?? [], 'orderNumber'), [act]);

  const performedAt = draft.performedAt ?? act?.performedAt;
  const notes = draft.notes ?? act?.notes ?? '';
  const transcript = draft.transcript ?? act?.transcript ?? '';

  const getSectionText = (section: AutopsyActSection) =>
    draft.sections[section.key] ?? section.editedText ?? section.generatedText ?? '';

  const setSectionText = (key: string, text: string) =>
    setDraft((prev) => ({ ...prev, sections: { ...prev.sections, [key]: text } }));

  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      isDirty && currentLocation.pathname !== nextLocation.pathname,
  );

  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      event.preventDefault();
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  const goBack = () =>
    window.history.state?.idx > 0 ? navigate(-1) : navigate(PATHOLOGY + ACTS);

  const saveDraft = async () => {
    if (!act || !isDirty) return;

    await updateAct({
      id: act.id,
      performedAt: draft.performedAt ?? act.performedAt,
      notes: draft.notes ?? act.notes,
      transcript: draft.transcript ?? act.transcript,
      sections: act.sections.map((section) => ({
        key: section.key,
        editedText:
          section.key in draft.sections
            ? draft.sections[section.key]
            : section.editedText,
      })),
    }).unwrap();
    setDraft(EMPTY_DRAFT);
  };

  const handleSave = async () => {
    try {
      await saveDraft();
      showSuccess('Акт сохранён');
    } catch {
      // ошибка уже показана в baseQueryWithReAuth
    }
  };

  const handleSign = async () => {
    if (!act) return;

    try {
      await saveDraft();
      await signAct({ id: act.id }).unwrap();
      setIsSignModalOpen(false);
      showSuccess('Акт подписан', 'Особь переведена в статус «Падеж»');
    } catch {
      // ошибка уже показана в baseQueryWithReAuth
    }
  };

  const handleRestartProcessing = async () => {
    if (!act) return;

    try {
      // правки расшифровки сохраняются до перезапуска, чтобы обработка шла по ним
      await saveDraft();
      await processAct({ id: act.id }).unwrap();
      showSuccess('Обработка записи запущена повторно');
    } catch {
      // ошибка уже показана в baseQueryWithReAuth
    }
  };

  const handlePrint = async () => {
    if (!act) return;

    try {
      // в печать уходит сохранённая версия, поэтому правки черновика сначала сохраняются
      if (can.edit) await saveDraft();
    } catch {
      return;
    }
    await print(act);
  };

  const handleReplaceAudio = async (values: AudioSourceValues) => {
    const attachment = toAudioAttachment(values);
    if (!act || !attachment) return;

    try {
      await replaceAudio({ id: act.id, audio: attachment }).unwrap();
      setIsAudioModalOpen(false);
      showSuccess('Запись загружена');
    } catch {
      // ошибка уже показана в baseQueryWithReAuth
    }
  };

  if (isError && !act) {
    return (
      <Result
        status="warning"
        title="Не удалось загрузить акт вскрытия"
        extra={<Button text="К списку актов" action={() => navigate(PATHOLOGY + ACTS)} />}
      />
    );
  }

  return (
    <>
      <PageTitle title={'Акт патологоанатомического вскрытия'} />
      <div className={cl.toolbar}>
        <Button text="Назад" variant={'outlined'} action={goBack} />
        <div className={cl.actions}>
          <Button
            text="Повторить обработку"
            icon={<SyncOutlined />}
            action={handleRestartProcessing}
            loading={isRestarting}
            display={can.edit && stage === ProcessingStage.FAILED}
          />
          <Button
            text={act?.hasAudio ? 'Заменить запись' : 'Загрузить запись'}
            action={() => setIsAudioModalOpen(true)}
            disabled={isProcessing}
            display={can.edit}
          />
          <Button
            type="primary"
            text="Сохранить"
            action={handleSave}
            loading={isSaving}
            disabled={!isDirty}
            display={can.edit}
          />
          <Button
            type="primary"
            text="Подписать"
            action={() => setIsSignModalOpen(true)}
            disabled={isProcessing}
            display={can.sign}
          />
          <Button
            type="primary"
            text="Печать PDF"
            action={handlePrint}
            loading={!!act && printingId === act.id}
            disabled={!act}
            display={can.print}
          />
        </div>
      </div>

      <Spin spinning={isLoading}>
        <div className={cl.content}>
          <AntCard className={cl.card}>
            <Descriptions
              bordered
              size="small"
              column={{ xs: 1, md: 2, xl: 3 }}
              items={[
                {
                  key: 'individual',
                  label: 'Особь',
                  children: act ? (
                    <Link to={VACCIANTION + REGISTRY + `/${act.individualId}`}>
                      {act.individualInventoryNumber}
                    </Link>
                  ) : null,
                },
                {
                  key: 'performedAt',
                  label: 'Дата вскрытия',
                  children: can.edit ? (
                    <DatePicker
                      allowClear={false}
                      locale={locale}
                      format={DateTimeFormat.DATE}
                      value={performedAt ? DateMapper(performedAt) : null}
                      disabledDate={(current: Dayjs) =>
                        current && current > DateMapper().endOf('day')
                      }
                      onChange={(value: Dayjs | null) =>
                        value &&
                        setDraft((prev) => ({
                          ...prev,
                          performedAt: value.format(DateTimeFormat.LOCAL_DATE),
                        }))
                      }
                    />
                  ) : (
                    formatDate(performedAt)
                  ),
                },
                {
                  key: 'veterinarian',
                  label: 'Ветеринарный врач',
                  children: act?.veterinarianFullName || '—',
                },
                {
                  key: 'status',
                  label: 'Статус',
                  children: <ActStatusTag status={act?.status} />,
                },
                {
                  key: 'processing',
                  label: 'Обработка записи',
                  children: (
                    <>
                      <ProcessingStageTag stage={stage} error={processingError} />
                      {!!act?.job && act.job.attempts > 1 && (
                        <Typography.Text type="secondary" className={cl.attempts}>
                          {`попытка ${act.job.attempts}`}
                        </Typography.Text>
                      )}
                    </>
                  ),
                },
                {
                  key: 'createdAt',
                  label: 'Дата создания',
                  children: formatDateTime(act?.createdAt),
                },
                ...(act?.signedAt
                  ? [
                      {
                        key: 'signedAt',
                        label: 'Дата подписания',
                        children: formatDateTime(act.signedAt),
                      },
                    ]
                  : []),
              ]}
            />
          </AntCard>

          {isProcessing && stage && (
            <Alert
              type="info"
              showIcon
              icon={<SyncOutlined spin />}
              message={`Идёт обработка записи: ${ProcessingStageLabel[stage].toLowerCase()}`}
              description="Разделы акта заполнятся автоматически, страница обновляется каждые 5 секунд."
            />
          )}
          {stage === ProcessingStage.FAILED && (
            <Alert
              type="error"
              showIcon
              message="Не удалось обработать запись"
              description={processingError || 'Причина ошибки не указана'}
            />
          )}

          <div>
            <PageTitle level={4} title={'Примечание'} subTitle={true} />
            <Input.TextArea
              value={notes}
              readOnly={!can.edit}
              autoSize={{ minRows: 2, maxRows: 8 }}
              placeholder={can.edit ? 'Введите примечание' : undefined}
              onChange={(event) =>
                setDraft((prev) => ({ ...prev, notes: event.target.value }))
              }
            />
          </div>

          {act?.hasAudio && (
            <div>
              <PageTitle level={4} title={'Аудиозапись'} subTitle={true} />
              <Spin spinning={isAudioFetching}>
                {audioUrl && (
                  // Текст записи показан ниже в блоке расшифровки, отдельных субтитров нет
                  <audio className={cl.audio} controls src={audioUrl}>
                    <track kind="captions" />
                  </audio>
                )}
              </Spin>
            </div>
          )}

          <Collapse
            items={[
              {
                key: 'transcript',
                label: act?.hasAudio ? 'Расшифровка записи' : 'Текст описания вскрытия',
                children: (
                  <Input.TextArea
                    value={transcript}
                    readOnly={!canEditContent}
                    autoSize={{ minRows: 4, maxRows: 20 }}
                    placeholder={
                      isProcessing
                        ? 'Расшифровка появится после распознавания'
                        : undefined
                    }
                    onChange={(event) =>
                      setDraft((prev) => ({ ...prev, transcript: event.target.value }))
                    }
                  />
                ),
              },
            ]}
          />

          <div>
            <PageTitle level={4} title={'Разделы акта'} subTitle={true} />
            {sections.length ? (
              <AutopsyActSections
                sections={sections}
                getText={getSectionText}
                onChange={setSectionText}
                readOnly={!canEditContent}
              />
            ) : (
              <Empty
                description={
                  isProcessing
                    ? 'Разделы появятся после обработки записи'
                    : 'Разделы акта не сформированы'
                }
              />
            )}
          </div>
        </div>
      </Spin>

      <Modal
        mode={'confirm'}
        open={isSignModalOpen}
        text={SIGN_CONFIRM_TEXT}
        submitText={'Подписать'}
        cancelText={'Отмена'}
        confirmLoading={isSaving || isSigning}
        onSubmit={handleSign}
        onCancel={() => setIsSignModalOpen(false)}
      />
      <Modal
        mode={'confirm'}
        open={blocker.state === 'blocked'}
        text={LEAVE_CONFIRM_TEXT}
        submitText={'Покинуть'}
        cancelText={'Остаться'}
        onSubmit={() => blocker.proceed?.()}
        onCancel={() => blocker.reset?.()}
      />
      <Modal
        open={isAudioModalOpen}
        mode={'form'}
        formKey={'replaceAutopsyAudio'}
        title={'Загрузить запись'}
        text={
          act?.hasAudio ? (
            <>
              <Typography.Title className={'modal-title'}>
                Заменить запись
              </Typography.Title>
              <Typography.Paragraph>
                Текущая запись акта будет заменена новой.
              </Typography.Paragraph>
            </>
          ) : undefined
        }
        data={{ source: AutopsySource.UPLOAD }}
        schema={audioSourceSchema([AutopsySource.UPLOAD, AutopsySource.RECORD])}
        onSubmit={handleReplaceAudio}
        onCancel={() => setIsAudioModalOpen(false)}
        width={'520px'}
        layout={{
          formLayout: 'vertical',
          itemLayout: 'vertical',
        }}
        buttons={{ submit: { text: 'Загрузить' } }}
        destroyOnHidden={true}
      />
    </>
  );
});
